'use client'

import { AutoAwesome } from '@mui/icons-material'
import { Button, IconButton, Tooltip, alpha, type ButtonProps } from '@mui/material'
import { useTranslations } from 'next-intl'

import { useGateValue } from '~/configs/statsig'

import { useOptionalBillingComposer } from './BillingComposerProvider'
import type { BillingComposerEntity, BillingComposerMode } from './types'

type ComposeWithAgentButtonProps = Pick<ButtonProps, 'fullWidth'> & {
  /** Sent as the first message when the panel opens. */
  prompt?: string
  /**
   * When set, renders a labeled text button (e.g. "Fix with Agent" on the hub card).
   * Without a label, renders the compact sparkle icon button with a tooltip — pattern 1a
   * beside the page primary action.
   */
  label?: string
}

/**
 * Entry point for the billing composer. Renders nothing unless both the `billing-composer` and
 * `ai-assistant` gates are on and a `BillingComposerProvider` that is enabled wraps the page.
 *
 * Default is a soft sparkle `IconButton` with a tooltip (smallest footprint next to Edit / Unarchive).
 * Pass `label` for a full text button when the surrounding chrome needs a readable CTA.
 */
export default function ComposeWithAgentButton({ prompt, label, fullWidth }: ComposeWithAgentButtonProps) {
  const t = useTranslations('features.BillingComposer')
  const composerGate = useGateValue('billing-composer')
  const assistantGate = useGateValue('ai-assistant')
  const composer = useOptionalBillingComposer()

  if (!composerGate || !assistantGate || !composer?.enabled) return null

  const tooltip = label ?? tooltipFor(t, composer.entity, composer.mode)

  if (label) {
    return (
      <Button
        startIcon={<AutoAwesome sx={{ fontSize: '14px !important' }} />}
        size="small"
        variant="outlined"
        color="inherit"
        fullWidth={fullWidth}
        onClick={() => composer.openComposer({ prompt })}
        data-cy="compose-with-agent"
        aria-expanded={composer.isOpen}
        sx={{
          alignSelf: 'center',
          minHeight: 28,
          px: 1.25,
          py: 0.25,
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: 'action.hover',
          color: 'text.secondary',
          fontSize: 13,
          fontWeight: 500,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          '& .MuiButton-startIcon': { mr: 0.5 },
          '&:hover': { borderColor: 'text.disabled', bgcolor: 'action.selected', color: 'text.primary' }
        }}
      >
        {label}
      </Button>
    )
  }

  return (
    <Tooltip title={tooltip}>
      <IconButton
        onClick={() => composer.openComposer({ prompt })}
        data-cy="compose-with-agent"
        aria-label={tooltip}
        aria-expanded={composer.isOpen}
        size="small"
        sx={{
          alignSelf: 'center',
          width: 36,
          height: 36,
          borderRadius: 1.5,
          color: 'primary.main',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16)
          }
        }}
      >
        <AutoAwesome fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

function tooltipFor(
  t: (key: 'tooltip_askAgreement' | 'tooltip_askProduct' | 'button_compose') => string,
  entity: BillingComposerEntity | undefined,
  mode: BillingComposerMode
): string {
  if (mode !== 'view') return t('button_compose')

  switch (entity) {
    case 'agreement':
      return t('tooltip_askAgreement')
    case 'product':
      return t('tooltip_askProduct')
    case undefined:
      return t('button_compose')
    default: {
      const _exhaustive: never = entity
      throw new Error(`Unhandled billing composer entity: ${String(_exhaustive)}`)
    }
  }
}
